import { getAccessToken, handleUnauthorizedError, refreshAccessToken } from '../utils/auth';

export type QueryParams = Record<string, string> | URLSearchParams;

export class ApiResponseError<E = any> extends Error {
    readonly status: number;
    readonly errors: E;
    constructor(status: number, error?: any) {
        super();
        this.errors = error?.errors ?? error;
        this.status = status;
    }
}

export class BadRequestError<E = any> extends ApiResponseError<E> {
    constructor(error: any) {
        super(400, error);
    }
}

export class UnauthorizedError<E = any> extends ApiResponseError<E> {
    constructor(error: any) {
        super(401, error);
    }
}

export class ForbiddenError<E = any> extends ApiResponseError<E> {
    constructor(error: any) {
        super(403, error);
    }
}

export class NotFoundError<E = any> extends ApiResponseError<E> {
    constructor(error: any) {
        super(404, error);
    }
}

export class TooManyRequestsError<E = any> extends ApiResponseError<E> {
    private _timeout: ReturnType<typeof setTimeout> | undefined;
    constructor(error: any) {
        super(429, error);
        if (error?.cfWaitingRoom || error?.vmpWaitingRoom) {
            this._timeout = setTimeout(() => location.reload(), 100);
        }
    }
    preventDefault() {
        clearTimeout(this._timeout);
    }
}

async function getContent(response: Response): Promise<any> {
    if (response.headers.get('Content-Type')?.includes('application/json')) {
        return await response.json();
    } else if (response.headers.get('Content-Disposition')?.startsWith('attachment')) {
        const name = response.headers.get('Content-Disposition')?.match(/attachment; filename="(.*)"/)?.[1];
        const blob = await response.blob();
        return { blob, name };
    } else {
        return await response.text();
    }
}

async function getError(response: Response) {
    return response
        .json<any>()
        .then((e) => e?.errors ?? e)
        .catch(() => 'Det har oppstått en feil.');
}

async function handleStatusCode<T>(response: Response): Promise<T> {
    if (response.ok) {
        return await getContent(response);
    } else {
        switch (response.status) {
            case 400: // Bad Request
                throw new BadRequestError(await getError(response));
            case 401: // Unauthorized
                handleUnauthorizedError();
                throw new UnauthorizedError(await getError(response));
            case 403: // Forbidden
                throw new ForbiddenError(await getError(response));
            case 404: // Not Found
                throw new NotFoundError(await getError(response));
            case 429: // Too Many Requests
                throw new TooManyRequestsError(await getError(response));
            default:
                throw await getError(response);
        }
    }
}

export async function get<TResponse = any>(url: string, query?: QueryParams, init?: RequestInit) {
    return fetchWithAuth(query ? `${url}?${new URLSearchParams(query)}` : url, {
        ...init,
        headers: getHeaders(init?.headers),
    }).then<TResponse>(handleStatusCode);
}

export async function getAnonymously<TResponse = any>(url: string, query?: QueryParams, init?: RequestInit) {
    return fetch(query ? `${url}?${new URLSearchParams(query)}` : url, {
        ...init,
        headers: getHeaders(init?.headers),
    }).then<TResponse>(handleStatusCode);
}

export async function httpDelete<TResponse = any>(url: string) {
    return fetchWithAuth(url, {
        method: 'DELETE',
        headers: getHeaders(),
    }).then<TResponse>(handleStatusCode);
}

export { httpDelete as delete };

export async function patch<TResponse = any>(url: string) {
    return fetchWithAuth(url, {
        method: 'PATCH',
        headers: getHeaders({ 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' }),
    }).then<TResponse>(handleStatusCode);
}

export async function patchJson<TResponse = any>(url: string, datain?: any) {
    return fetchWithAuth(url, {
        method: 'PATCH',
        headers: getHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(datain),
    }).then<TResponse>(handleStatusCode);
}

export async function post<TResponse = any>(url: string, datain?: any) {
    const params = new URLSearchParams(datain);

    return fetchWithAuth(url, {
        method: 'POST',
        body: params,
        headers: getHeaders({ 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' }),
    }).then<TResponse>(handleStatusCode);
}

export async function postJson<TResponse = any, TBody = any>(url: string, datain?: TBody) {
    return fetchWithAuth(url, {
        method: 'POST',
        headers: getHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(datain),
    }).then<TResponse>(handleStatusCode);
}

export async function put<TResponse = any>(url: string, datain?: any) {
    return fetchWithAuth(url, {
        method: 'PUT',
        body: new URLSearchParams(datain),
        headers: getHeaders({ 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' }),
    }).then<TResponse>(handleStatusCode);
}

export async function putJson<TResponse = any>(url: string, datain?: any) {
    return fetchWithAuth(url, {
        method: 'PUT',
        headers: getHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(datain),
    }).then<TResponse>(handleStatusCode);
}

async function fetchWithAuth(url: string, init: RequestInit & { headers: Headers }): Promise<Response> {
    // Set the authorization header
    setAuthorizationHeader(init.headers);

    // Make the request
    const response = await fetch(url, init);

    // Check if we are unauthorized
    if (response.status !== 401) return response;

    // If we are unauthorized, try to refresh the token
    if (!(await refreshAccessToken())) return response;

    // Set the auth header again
    setAuthorizationHeader(init.headers);

    // And rerun the request
    return await fetch(url, init);
}

function getHeaders(initial?: HeadersInit) {
    const headers = initial instanceof Headers ? initial : new Headers(initial);
    headers.set('Accept', 'application/json');
    return headers;
}

function setAuthorizationHeader(headers: Headers) {
    const accessToken = getAccessToken();

    if (accessToken) {
        // Overwrite if it is already there
        headers.set('authorization', `Bearer ${accessToken}`);
    } else {
        // Remove it if it is there
        headers.delete('authorization');
    }
}
